import React, { useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { getPortfolioUpdateChangesAppliedKpis } from 'gqlQueries/priceSimulator';
import { portfolioUpdateKpiParser } from 'parser/priceSimulator/priceSimulatorDataParser';
import { KpiMetricType } from 'contexts/PriceSimulatorContext';
import { getPercentageChange } from 'utils/helperMethods';
import _ from 'lodash';
const errorMessage: string = 'Something went wrong!';

type props = {
    setKpiMetrictsState: Function;
    setPortfolioUpdateKpisData: Function;
    setBackUpPortfolioUpdateKpisData: Function;
    kpiMetrictsState: any;
};
function useFetchPortfolioUpdateChangesAppliedKpis({
    setKpiMetrictsState,
    setPortfolioUpdateKpisData,
    setBackUpPortfolioUpdateKpisData,
    kpiMetrictsState,
}: props) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any>(null);
    const offsetRef = useRef(0);

    const [fetchPortfolioUpdateChangesAppliedKpis] = useLazyQuery(getPortfolioUpdateChangesAppliedKpis, {
        onCompleted: onFetchComplete,
        onError: onFetchError,
        fetchPolicy: 'cache-and-network',
    });

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const { getPortfolioUpdateChangesAppliedKpis } = payload;

            if (getPortfolioUpdateChangesAppliedKpis) {
                if ('totalKpisData' in getPortfolioUpdateChangesAppliedKpis) {
                    const newKpiMetrics = kpiMetrictsState.map((item: KpiMetricType) => {
                        const actualValue = Math.ceil(
                            getPortfolioUpdateChangesAppliedKpis.totalKpisData.find(
                                (payloadData: any) => payloadData.kpiName === item.value,
                            ).kpiValue,
                        ).toString();
                        return {
                            ...item,
                            actualValue,
                            percentChange: getPercentageChange(
                                Number(item.goalValue || item.actualValue),
                                Number(actualValue),
                            ),
                            goalValue: item.goalValue,
                            simulatedValue: item.simulatedValue,
                        };
                    });
                    setKpiMetrictsState(newKpiMetrics);
                }

                if ('portfolioUpdateData' in getPortfolioUpdateChangesAppliedKpis) {
                    const parsedPortfolioUpdateKpiData = getPortfolioUpdateChangesAppliedKpis.portfolioUpdateData?.map(
                        (item: any) => JSON.parse(item),
                    );
                    const parsedData = portfolioUpdateKpiParser(parsedPortfolioUpdateKpiData);
                    setPortfolioUpdateKpisData(_.cloneDeep(parsedData));
                    setBackUpPortfolioUpdateKpisData(_.cloneDeep(parsedData));
                    setData(parsedData);
                }

                return;
            }
            setData(null);
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData([]);
        setLoading(false);
        setError(error);
    }

    return {
        loading,
        error,
        data,
        fetchPortfolioUpdateChangesAppliedKpis: (variables: any) => {
            offsetRef.current = variables.variables.offset;
            setLoading(true);
            setError(null);
            fetchPortfolioUpdateChangesAppliedKpis(variables);
        },
    };
}

export { useFetchPortfolioUpdateChangesAppliedKpis };
